<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
/* global axios */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const loading = ref(true);
const cases = ref([]);
const counts = ref({});
const activeStage = ref(String(route.query.stage || 'all'));

const accountId = computed(() => route.params.accountId);
const caseType = computed(() => String(route.params.caseType));
const configuration = computed(() => {
  const settings = {
    urgent: {
      eyebrow: 'Atención prioritaria',
      title: 'Incidencias urgentes',
      description: 'Casos graves que necesitan una respuesta especial.',
      icon: '🚨',
      tabs: [{ key: 'all', label: 'Todas' }],
    },
    shipping: {
      eyebrow: 'Envíos pendientes',
      title: 'Problemas de envío',
      description: 'Pedidos con una incidencia de transporte todavía abierta.',
      icon: '📦',
      tabs: [
        { key: 'needs_action', label: 'Necesitan actuación' },
        { key: 'waiting', label: 'Esperando' },
        { key: 'all', label: 'Todos' },
      ],
    },
    returns: {
      eyebrow: 'Gestiones pendientes',
      title: 'Cambios y devoluciones',
      description: 'Procesos ya iniciados que todavía no han terminado.',
      icon: '🔄',
      tabs: [
        { key: 'needs_action', label: 'Necesitan actuación' },
        { key: 'waiting_customer', label: 'Esperando clienta' },
        { key: 'in_progress', label: 'En proceso' },
        { key: 'all', label: 'Todos' },
      ],
    },
  };
  return settings[caseType.value] || settings.urgent;
});

const formatDate = value =>
  new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const stageLabel = stage => {
  const labels = {
    needs_action: 'Necesita actuación',
    waiting: 'Esperando',
    waiting_customer: 'Esperando clienta',
    in_progress: 'En proceso',
  };
  return labels[stage] || 'Pendiente';
};

const loadCases = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/dashboard_cases`,
      {
        params: {
          case_type: caseType.value,
          stage: activeStage.value === 'all' ? undefined : activeStage.value,
        },
      }
    );
    cases.value = data.cases || [];
    counts.value = data.counts || {};
  } finally {
    loading.value = false;
  }
};

const selectStage = stage => {
  activeStage.value = stage;
  router.replace({ query: { ...route.query, stage } });
};

const openConversation = item => {
  const fullPath = router.resolve({
    name: 'mi_marciana_cases',
    params: { accountId: accountId.value, caseType: caseType.value },
    query: { stage: activeStage.value },
  }).fullPath;
  window.sessionStorage.setItem(
    `mm-chatwoot:conversation-return:${accountId.value}:${item.id}`,
    JSON.stringify({ conversationId: item.id, fullPath })
  );
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: item.id },
    query: { mm_return_to: fullPath },
  });
};

const resolveCase = async item => {
  await axios.patch(
    `/api/v1/accounts/${accountId.value}/dashboard_cases/${item.id}`,
    { case_type: caseType.value, status: 'resolved', stage: item.stage }
  );
  await loadCases();
};

watch(caseType, () => {
  activeStage.value = 'all';
  loadCases();
});
onMounted(loadCases);
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-n-background text-n-slate-12">
    <header class="shrink-0 border-b border-n-weak bg-n-solid-1 px-4 py-4 md:px-7">
      <div class="mx-auto flex max-w-5xl items-start gap-3">
        <button
          type="button"
          class="grid size-10 shrink-0 place-items-center rounded-full bg-n-alpha-2 text-xl hover:bg-n-alpha-3"
          aria-label="Volver al panel"
          @click="router.push({ name: 'home', params: { accountId } })"
        >
          ‹
        </button>
        <div class="min-w-0 flex-1">
          <p class="text-xs font-bold uppercase tracking-widest text-n-slate-9">
            {{ configuration.eyebrow }}
          </p>
          <h1 class="mt-1 text-2xl font-bold">
            {{ configuration.icon }} {{ configuration.title }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-10">
            {{ configuration.description }}
          </p>
        </div>
      </div>
      <nav
        v-if="configuration.tabs.length > 1"
        class="mx-auto mt-4 flex max-w-5xl gap-2 overflow-x-auto"
        aria-label="Estado del caso"
      >
        <button
          v-for="tab in configuration.tabs"
          :key="tab.key"
          type="button"
          class="shrink-0 rounded-full px-3 py-2 text-xs font-semibold"
          :class="activeStage === tab.key ? 'bg-n-slate-12 text-white' : 'bg-n-alpha-2 text-n-slate-11'"
          @click="selectStage(tab.key)"
        >
          {{ tab.label }}
          <span v-if="counts[tab.key] !== undefined">· {{ counts[tab.key] }}</span>
        </button>
      </nav>
    </header>

    <main class="min-h-0 flex-1 overflow-y-auto p-3 md:p-6">
      <div class="mx-auto grid max-w-5xl gap-3">
        <div v-if="loading" class="rounded-2xl bg-n-solid-1 p-8 text-center text-n-slate-10">
          Cargando casos pendientes…
        </div>
        <div
          v-else-if="!cases.length"
          class="rounded-2xl border border-dashed border-n-weak bg-n-solid-1 p-10 text-center"
        >
          <div class="text-4xl">✓</div>
          <h2 class="mt-3 text-lg font-bold">No hay casos pendientes aquí</h2>
          <p class="mt-1 text-sm text-n-slate-10">Todo lo de este apartado está controlado.</p>
        </div>

        <article
          v-for="item in cases"
          :key="item.id"
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <div class="flex items-start gap-3">
            <img
              v-if="item.customer_avatar_url"
              :src="item.customer_avatar_url"
              :alt="item.customer_name"
              class="size-11 shrink-0 rounded-full object-cover"
            />
            <div
              v-else
              class="grid size-11 shrink-0 place-items-center rounded-full bg-pink-100 font-bold text-pink-700"
            >
              {{ item.customer_name?.slice(0, 2).toUpperCase() }}
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h2 class="truncate font-bold">{{ item.customer_name }}</h2>
                <span v-if="item.order_number" class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-semibold">
                  Pedido {{ item.order_number }}
                </span>
                <span class="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  {{ stageLabel(item.stage) }}
                </span>
              </div>
              <p class="mt-2 line-clamp-2 text-sm font-medium">{{ item.summary }}</p>
              <p v-if="caseType === 'urgent'" class="mt-2 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-800">
                <strong>Por qué es urgente:</strong> {{ item.reason }}
              </p>
              <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-10">
                <span>{{ item.inbox_name }}</span>
                <span>Responsable: {{ item.responsible?.name || 'Sin asignar' }}</span>
                <span>{{ formatDate(item.updated_at) }}</span>
              </div>
            </div>
          </div>
          <div class="mt-4 flex gap-2 border-t border-n-weak pt-3">
            <button
              type="button"
              class="flex-1 rounded-xl bg-n-brand px-4 py-2.5 text-sm font-bold text-white hover:opacity-90"
              @click="openConversation(item)"
            >
              ABRIR
            </button>
            <button
              type="button"
              class="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-800"
              @click="resolveCase(item)"
            >
              Resolver
            </button>
          </div>
        </article>
      </div>
    </main>
  </div>
</template>
